import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { usePaginatedHiringFeed } from "@src/appV2/redesign/HiringFeed/api/usePaginatedHiringFeed";
import { useFetchDiscoveryPlacements } from "@src/appV2/redesign/Placements/api/useFetchDiscoveryPlacements";
import { isEmpty } from "lodash";

interface UsePlacementsListDataParams {
  workerId: string;
  placementCandidateId: string;
  placementFilters: Parameters<typeof useFetchDiscoveryPlacements>[0]["filter"];
  workerTypesAvailableForHiring?: string[];
  showApplications: boolean;
}

export function usePlacementsListData(params: UsePlacementsListDataParams) {
  const {
    workerId,
    placementCandidateId,
    placementFilters,
    workerTypesAvailableForHiring,
    showApplications,
  } = params;

  const isDiscoveryMigrationEnabled = useCbhFlag(CbhFeatureFlag.PLACEMENTS_DISCOVERY_MIGRATION, {
    defaultValue: false,
  });

  // Gate on placementFilters (the processed, worker-type-resolved filter) rather than
  // the raw filter. placementFilters is undefined until availableWorkerTypes loads, so
  // gating on the raw filter would issue an unfiltered request during that window.
  const isQueryReady = isDefined(workerId) && !showApplications && !isEmpty(placementFilters);

  const hiringFeedResults = usePaginatedHiringFeed(
    {
      workerId,
      placementCandidateId,
      filter: placementFilters,
      workerTypesAvailableForHiring,
    },
    {
      enabled: !isDiscoveryMigrationEnabled && isQueryReady,
    }
  );

  const discoveryResults = useFetchDiscoveryPlacements(
    {
      workerId,
      placementCandidateId,
      filter: placementFilters,
      workerTypesAvailableForHiring,
    },
    {
      enabled: isDiscoveryMigrationEnabled && isQueryReady,
    }
  );

  return { isDiscoveryMigrationEnabled, hiringFeedResults, discoveryResults };
}
